import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getApiInstance } from "../../../utils/api";
import Button from "../../common/Button/Button";
import Input from "../../common/Inputs/Input/Input";
import { FaCheckCircle, FaLock } from "react-icons/fa";
import "./ResetPassword.scss";

const ResetPassword = ({ token, onSuccess, onCancel }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        password: "",
        confirmPassword: "",
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const validatePassword = (password) => {
        const errors = {};

        if (password.length < 8) {
            errors.length = t("resetPassword.validation.minLength");
        }
        if (!/[A-Z]/.test(password)) {
            errors.uppercase = t("resetPassword.validation.uppercase");
        }
        if (!/[a-z]/.test(password)) {
            errors.lowercase = t("resetPassword.validation.lowercase");
        }
        if (!/[0-9]/.test(password)) {
            errors.number = t("resetPassword.validation.number");
        }
        if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
            errors.special = t("resetPassword.validation.special");
        }

        return errors;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));

        if (name === "password") {
            const errors = validatePassword(value);
            setValidationErrors(errors);
        }

        setError("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");

        console.log("Submitting form with data:", formData);
        console.log("Password:", formData.password);

        // Validar que las contraseñas coincidan
        if (formData.password !== formData.confirmPassword) {
            setError(t("resetPassword.passwordMismatch"));
            return;
        }

        // Validar requisitos de contraseña
        const passwordErrors = validatePassword(formData.password);
        if (Object.keys(passwordErrors).length > 0) {
            setError(t("resetPassword.passwordRequirements"));
            return;
        }

        setLoading(true);

        try {
            const api = getApiInstance();
            const payload = new URLSearchParams();
            payload.append("token", token);
            payload.append("password", formData.password);

            await api.post("api/Auth/UpdateForgotPassword", payload, {
                headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                },
            });
            setSuccess(true);
        } catch (err) {
            setError(err.response?.data?.message || t("resetPassword.error"));
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="reset-password__success">
                <FaCheckCircle className="reset-password__success__icon" />
                <h2>{t("resetPassword.successTitle")}</h2>
                <p>{t("resetPassword.successMessage")}</p>
                <Button variant="filled" onClick={onSuccess}>
                    Volver al inicio de sesión
                </Button>
            </div>
        );
    }

    return (
        <div className="reset-password">
            <div className="reset-password__header">
                <FaLock className="reset-password__header__icon" />
                <h2>{t("resetPassword.title")}</h2>
            </div>

            <p className="reset-password__subtitle">
                {t("resetPassword.subtitle")}
            </p>

            <form onSubmit={handleSubmit} className="reset-password__form">
                <Input
                    type="password"
                    name="password"
                    label={t("resetPassword.passwordLabel")}
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t("resetPassword.passwordPlaceholder")}
                    required
                    disabled={loading}
                />

                {formData.password &&
                    Object.keys(validationErrors).length > 0 && (
                        <div className="reset-password__requirements">
                            <p className="reset-password__requirements__title">
                                {t("resetPassword.requirementsTitle")}
                            </p>
                            <ul className="reset-password__requirements__list">
                                <li
                                    className={
                                        !validationErrors.length
                                            ? "reset-password__requirements__list__item--valid"
                                            : "reset-password__requirements__list__item"
                                    }
                                >
                                    {t("resetPassword.validation.minLength")}
                                </li>
                                <li
                                    className={
                                        !validationErrors.uppercase
                                            ? "reset-password__requirements__list__item--valid"
                                            : "reset-password__requirements__list__item"
                                    }
                                >
                                    {t("resetPassword.validation.uppercase")}
                                </li>
                                <li
                                    className={
                                        !validationErrors.lowercase
                                            ? "reset-password__requirements__list__item--valid"
                                            : "reset-password__requirements__list__item"
                                    }
                                >
                                    {t("resetPassword.validation.lowercase")}
                                </li>
                                <li
                                    className={
                                        !validationErrors.number
                                            ? "reset-password__requirements__list__item--valid"
                                            : "reset-password__requirements__list__item"
                                    }
                                >
                                    {t("resetPassword.validation.number")}
                                </li>
                                <li
                                    className={
                                        !validationErrors.special
                                            ? "reset-password__requirements__list__item--valid"
                                            : "reset-password__requirements__list__item"
                                    }
                                >
                                    {t("resetPassword.validation.special")}
                                </li>
                            </ul>
                        </div>
                    )}

                <Input
                    type="password"
                    name="confirmPassword"
                    label={t("resetPassword.confirmPasswordLabel")}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder={t("resetPassword.confirmPasswordPlaceholder")}
                    required
                    disabled={loading}
                />

                {error && <div className="reset-password__error">{error}</div>}

                <Button
                    type="submit"
                    variant="filled"
                    disabled={
                        loading ||
                        !formData.password ||
                        !formData.confirmPassword
                    }
                    loading={loading}
                >
                    {t("resetPassword.submit")}
                </Button>

                {onCancel && (
                    <button
                        type="button"
                        className="reset-password__back"
                        onClick={onCancel}
                    >
                        {t("resetPassword.cancel")}
                    </button>
                )}
            </form>
        </div>
    );
};

export default ResetPassword;
